/**
 * Initial values for an action's user-input dialog, from each parameter's `Value` column.
 *
 * meta_action_parameter.value has always existed, but the dialog opened empty and ignored it for user input. With
 * this helper a parameter's Value becomes the field's starting value, so a report dialog can open with a box ticked
 * or a date filled in. The user can still change it before Submit.
 *
 *   control type      Value                         initial value
 *   checkbox / bool   true, yes, 1, on  (any case)  true
 *                     false, no, 0, off             false
 *   date              Today, Yesterday, FirstOfCurrentMonth ... (convertDateExpressionToDate's names)
 *                     or an ISO date (2026-09-21)   'YYYY-MM-DD'
 *   anything else     the text as written - only when the parameter HAS a control type
 *
 * A blank Value (null, '' or whitespace) gives no entry, so the field starts empty as before. Older rows that used
 * Value as a label with no control type (e.g. Send Email's 'Email Body', 'CC') get no default, so nothing changes
 * for them.
 */

export interface UserInputParameterLike {
  Key: string;
  Value?: string | null;
  ControlType?: string | null;
}

const TRUE_WORDS = new Set(["true", "yes", "1", "on", "y"]);
const FALSE_WORDS = new Set(["false", "no", "0", "off", "n"]);
const BOOL_CONTROLS = new Set(["checkbox", "bool", "boolean", "boolselect", "switch", "toggle"]);
const DATE_CONTROLS = new Set(["date", "dateselect"]);

const pad = (n: number) => String(n).padStart(2, "0");

function isoDay(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The named date expressions (same names as utils/date.ts convertDateExpressionToDate). */
export function dateExpressionToIso(expression: string, now: Date = new Date()): string | undefined {
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const at = (yy: number, mm: number, dd: number) => isoDay(new Date(yy, mm, dd));
  switch (expression.trim().toLowerCase()) {
    case "today": return at(y, m, d);
    case "tomorrow": return at(y, m, d + 1);
    case "yesterday": return at(y, m, d - 1);
    case "firstofcurrentmonth": return at(y, m, 1);
    case "firstofnextmonth": return at(y, m + 1, 1);
    case "firstoflastmonth": return at(y, m - 1, 1);
    case "lastofcurrentmonth": return at(y, m + 1, 0);
    case "lastofnextmonth": return at(y, m + 2, 0);
    case "lastoflastmonth": return at(y, m, 0);
    case "firstofcurrentyear": return at(y, 0, 1);
    case "firstofnextyear": return at(y + 1, 0, 1);
    case "firstoflastyear": return at(y - 1, 0, 1);
    default: return undefined;
  }
}

export function userInputDefaultValue(
  param: UserInputParameterLike,
  now: Date = new Date(),
): unknown {
  const raw = param.Value;
  if (raw === null || raw === undefined) return undefined;
  const text = String(raw).trim();
  if (text === "") return undefined;

  const control = (param.ControlType ?? "").trim().toLowerCase();

  if (BOOL_CONTROLS.has(control)) {
    const word = text.toLowerCase();
    if (TRUE_WORDS.has(word)) return true;
    if (FALSE_WORDS.has(word)) return false;
    return undefined; // a value we cannot read as yes/no: leave the box unset rather than guess
  }

  if (DATE_CONTROLS.has(control)) {
    const named = dateExpressionToIso(text, now);
    if (named) return named;
    if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.substring(0, 10);
    return undefined;
  }

  // Older rows used Value as a label with no control type (e.g. Send Email's 'Email Body', 'CC'); pre-filling
  // those would put the label into the field and send it. Only a parameter with a control type gets a text default.
  if (control === "") return undefined;

  return text;
}

/** { Key: initial value } for every parameter that has a usable Value. */
export function userInputDefaults(
  params: UserInputParameterLike[] | null | undefined,
  now: Date = new Date(),
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const p of params ?? []) {
    if (!p?.Key) continue;
    const v = userInputDefaultValue(p, now);
    if (v !== undefined) out[p.Key] = v;
  }
  return out;
}
